import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["activeUsers", "activityLog", "userCount"]

  connect() {
    this.setupAppearanceChannel()
    this.setupActivityChannel()
    this.setupInitialReactionButtons()
  }

  disconnect() {
    if (this.appearanceSubscription) {
      this.appearanceSubscription.unsubscribe()
    }
    if (this.activitySubscription) {
      this.activitySubscription.unsubscribe()
    }
  }

  setupAppearanceChannel() {
    const controller = this

    this.appearanceSubscription = window.consumer.subscriptions.create("AppearanceChannel", {
      received(data) {
        if (data.type === "presence_update") {
          controller.updateActiveUsers(data.active_users, data.count)
        } else if (data.type === "presence_reaction") {
          controller.showReactionAnimation(data.target_user_id, data.emoji, data.sender_name)
        }
      }
    })
  }

  setupActivityChannel() {
    const controller = this

    this.activitySubscription = window.consumer.subscriptions.create("ActivityChannel", {
      received(data) {
        if (data.type === "new_activity") {
          controller.prependActivity(data.activity)
        }
      }
    })
  }

  updateActiveUsers(users, count) {
    const target = this.hasActiveUsersTarget ? this.activeUsersTarget : null
    if (!target) return

    if (users.length === 0) {
      target.innerHTML = '<span class="text-sm text-gray-400 italic pl-2">現在は誰もいません...</span>'
      return
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    const html = users.map(user => `
      <div class="relative group" data-user-id="${user.id}">
        <a href="${user.profile_path}" class="cursor-pointer">
          <div class="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-xs font-bold border-2 border-white dark:border-gray-800 transition-transform hover:scale-110 hover:z-10">
            ${user.initial}
          </div>
          <div class="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-gray-800 ${user.status === 'online' ? 'bg-green-400' : 'bg-yellow-400'}"></div>
        </a>
        <div class="absolute top-full mt-2 left-1/2 -translate-x-1/2 px-3 py-2 bg-black/90 backdrop-blur text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none group-hover:pointer-events-auto shadow-xl border border-white/10">
          <div class="font-bold mb-0.5 flex items-center gap-1.5">
            ${user.name}
            ${user.role === 'staff' ? '<span class="px-1 py-0.5 rounded text-[9px] font-bold bg-blue-500 text-white">Staff</span>' : ''}
          </div>
          <div class="text-gray-300 flex items-center gap-1">
            <span class="w-1.5 h-1.5 rounded-full ${user.status === 'online' ? 'bg-green-400' : 'bg-yellow-400'}"></span>
            ${user.status === 'online' ? 'オンライン' : user.status}
          </div>
          ${user.skill ? `<div class="text-gray-400 mt-0.5">📖 ${user.skill}学習中</div>` : ''}
          <div class="flex gap-1.5 mt-2 pt-2 border-t border-white/10">
            ${['👀', '🔥', '🍋'].map(emoji => `
              <button class="presence-reaction-btn w-7 h-7 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 hover:scale-110 active:scale-95 transition-all cursor-pointer text-sm"
                      data-user-id="${user.id}"
                      data-emoji="${emoji}">
                ${emoji}
              </button>
            `).join('')}
          </div>
        </div>
        <div class="reaction-float-container absolute -top-2 left-1/2 -translate-x-1/2 pointer-events-none"></div>
      </div>
    `).join("")

    target.innerHTML = html

    // リアクションボタンにイベントリスナーを追加
    target.querySelectorAll('.presence-reaction-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        const userId = btn.dataset.userId
        const emoji = btn.dataset.emoji
        this.sendPresenceReaction(userId, emoji, csrfToken)
      })
    })

    // Update count if target exists
    if (this.hasUserCountTarget) {
      this.userCountTarget.textContent = count
    }
  }

  sendPresenceReaction(userId, emoji, csrfToken) {
    fetch('/presence_reactions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify({ user_id: userId, emoji: emoji })
    })
    .then(res => res.json())
    .then(data => {
      if (data.error) {
        console.warn('[PresenceReaction]', data.error)
      }
    })
    .catch(err => console.error('[PresenceReaction] Error:', err))
  }

  setupInitialReactionButtons() {
    const target = this.hasActiveUsersTarget ? this.activeUsersTarget : null
    if (!target) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    target.querySelectorAll('.presence-reaction-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        const userId = btn.dataset.userId
        const emoji = btn.dataset.emoji
        this.sendPresenceReaction(userId, emoji, csrfToken)
      })
    })
  }

  showReactionAnimation(targetUserId, emoji, senderName) {
    const target = this.hasActiveUsersTarget ? this.activeUsersTarget : null
    if (!target) return

    const userEl = target.querySelector(`[data-user-id="${targetUserId}"]`)
    if (!userEl) return

    const container = userEl.querySelector('.reaction-float-container')
    if (!container) return

    // 絵文字をふわっと浮かせるアニメーション
    const floater = document.createElement('div')
    floater.textContent = emoji
    floater.className = 'reaction-floater'
    floater.style.cssText = `
      position: absolute;
      font-size: 1.25rem;
      animation: reaction-float-up 1.5s ease-out forwards;
      pointer-events: none;
      left: 50%;
      transform: translateX(-50%);
      z-index: 100;
    `
    container.appendChild(floater)

    // アニメーション終了後に要素を削除
    setTimeout(() => floater.remove(), 1500)
  }

  prependActivity(activity) {
    const target = this.hasActivityLogTarget ? this.activityLogTarget : null
    if (!target) return

    // Remove "no activity" message if exists
    const emptyMsg = target.querySelector("[data-empty-message]")
    if (emptyMsg) emptyMsg.remove()

    const html = `
      <div class="relative animate-slide-in" data-activity-id="${activity.id}">
        <div class="absolute -left-[31px] bg-white dark:bg-gray-800 p-1">
          <div class="h-4 w-4 rounded-full bg-green-400 border-2 border-white dark:border-gray-800 animate-pulse"></div>
        </div>
        <div class="flex items-center gap-2 mb-0.5">
          <div class="w-5 h-5 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0">
            ${activity.user_initial}
          </div>
          <p class="text-sm text-gray-800 dark:text-gray-200">
            <span class="font-bold">${activity.user_name}</span>
            <span class="ml-1">${activity.icon}</span>
            ${activity.label}
          </p>
        </div>
        <span class="text-xs text-gray-400 ml-7">${activity.time_ago}</span>
      </div>
    `

    // Add to top of the list
    target.insertAdjacentHTML("afterbegin", html)

    // Remove the pulse animation after 3 seconds
    setTimeout(() => {
      const newItem = target.querySelector(`[data-activity-id="${activity.id}"] .animate-pulse`)
      if (newItem) {
        newItem.classList.remove("animate-pulse")
        newItem.classList.replace("bg-green-400", "bg-blue-400")
      }
    }, 3000)

    // Limit displayed items to 15
    const items = target.querySelectorAll("[data-activity-id]")
    if (items.length > 15) {
      items[items.length - 1].remove()
    }
  }
}
